<!--Компонент для отображения верхнего меню-->
<template>
  <v-app-bar :elevation="0" color="corporate_main" height="78" class="top_menu">
    <template v-slot:prepend>
      <LeftMenuToggle
        v-if="leftmenu.enabled"
        @toggle="leftMenuToggle"
      />
    </template>
    <AppBarTitle
      :title="maintoolbar.title"
      :logo="logo"
    />
    <v-spacer/>
    <template v-slot:append>
      <div class="top_menu__button text-white d-flex">
        <MainInstancesDesktop
          v-if="$vuetify.display.width > 1560"
          :items="maintoolbar.mainInstances"
          @item-click="dispatchRouter"
        />
        <ServiceMenu
          :items="maintoolbar.secondInstances"
          @item-click="routerPath"
        />
        <EventAlerts/>
        <UserMenu
          :user="user"
          :avatar="maintoolbar.userAvatar"
          :actions="maintoolbar.userActions"
          :accounts="accountAccounts"
          v-if="isUserAuthenticated"
          @account-change="changeUserAccount"
          @action-click="routerPath"
          @logout="logout"
        />
        <LoginButton
          @click="routerLogin"
          v-else/>
      </div>
    </template>
  </v-app-bar>
</template>

<script>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import logo from '@/assets/logo.png'
import { goToLoginPage } from '@/helper/auth'
import names from '@/modules/profile/routers/names'
import { useRouter } from "vue-router"
import EventAlerts from '@/modules/templates/EventAlerts.vue'
import { checkIsDevelopmentServer } from "@/helper";
import LeftMenuToggle from "@/modules/core/components/menus/top_menu/LeftMenuToggle.vue";
import AppBarTitle from "@/modules/core/components/menus/top_menu/AppBarTitle.vue";
import MainInstancesDesktop from "@/modules/core/components/menus/top_menu/MainInstancesDekstop.vue";
import ServiceMenu from "@/modules/core/components/menus/top_menu/ServiceMenu.vue";
import UserMenu from "@/modules/core/components/menus/top_menu/UserMenu.vue";
import LoginButton from "@/modules/core/components/menus/top_menu/LoginButton.vue";

/**
 * Компонент верхнего меню приложения
 * Содержит навигацию, меню пользователя и управление левым меню
 */
export default {
  name: 'TopMenu',

  components: {
    LoginButton,
    UserMenu,
    ServiceMenu,
    AppBarTitle,
    EventAlerts,
    LeftMenuToggle,
    MainInstancesDesktop,
  },

  /**
   * Настраивает реактивные свойства и методы компонента
   * @returns {Object} объект с реактивными свойствами и методами
   */
  setup () {
    const store = useStore()
    const router = useRouter()

    const checkIsDevelopment = checkIsDevelopmentServer()

    // Реактивные ссылки
    const logoRef = ref(logo)
    const namesRef = ref(names)

    // Вычисляемые свойства
    const accountAccounts = computed(() => store.getters['selectors/accountAccounts'])
    const isUserAuthenticated = computed(() => store.getters['auth/isAuthenticate'])
    const leftmenu = computed(() => store.state.leftmenu)
    const maintoolbar = computed(() => store.state.maintoolbar)
    const user = computed(() => store.state.user)

    /**
     * Сменяет аккаунт пользователя
     * @param {string} value - идентификатор аккаунта
     */
    const changeUserAccount = function (value) {
      store.dispatch('user/changeAccount', {
        data: { user: value },
        finalizer: () => {
        },
        catcher: () => {
        }
      })
    }

    /**
     * Перенаправляет на страницу входа
     */
    const routerLogin = () => {
      goToLoginPage()
    }

    /**
     * Обрабатывает навигацию по различным типам элементов
     * @param {Object} item - элемент навигации
     */
    const routerPath = (item) => {
      if (item.router) {
        router.push({ name: item.router })
      } else if (item.url) {
        window.open(item.url, '_blank')
      } else {
        router.push({ name: item.name })
      }
    }

    /**
     * Обрабатывает навигацию (для совместимости с Options API)
     * @param {Object} item - элемент навигации
     */
    const dispatchRouter = (item) => {
      if (item.router) {
        router.push({ name: item.router })
      }
    }

    /**
     * Переключает видимость левого меню
     */
    const leftMenuToggle = () => {
      store.commit('leftmenu/toggleVisible')
    }

    /**
     * Выполняет выход из аккаунта
     * @param {boolean} dissable_left - отключать ли левое меню
     * @param {boolean} reload_page - перезагружать ли страницу
     * @param {boolean} redirect - выполнять ли редирект
     */
    const logout = (dissable_left = true, reload_page = false, redirect = true) => {
      store.commit('urls/clearUrls')
      if (dissable_left) {
        store.commit('leftmenu/dissable')
      }
      store.commit('leftmenu/replaceItems', [])
      store.dispatch('auth/AUTH_LOGOUT')
        .then(() => {
          if (redirect) {
            document.location.href = process.env.VUE_APP_LOGIN_HOST
          }
          if (reload_page) {
            window.location.reload()
          }
        })
    }

    return {
      logo: logoRef,
      names: namesRef,
      isUserAuthenticated,
      leftmenu,
      maintoolbar,
      user,
      accountAccounts,
      changeUserAccount,
      routerLogin,
      routerPath,
      dispatchRouter,
      leftMenuToggle,
      logout,
      checkIsDevelopment
    }
  },
}
</script>

<style scoped lang="scss">
.top_menu {
  background-image: url('@/assets/bg_top_menu.svg'), linear-gradient(90deg, var(--corporate-main) 0%, var(--blue-shades-dark) 100%, var(--blue-shades-dark) 100%) !important;
  background-position: 90% 0, center !important;
  background-repeat: no-repeat !important;
}

@media screen and (max-width: 1919px) {
  .top_menu {
    background-position: 700px 0, center !important;
  }
}

@media screen and (max-width: 1279px) {
  .top_menu {
    background-position: 500px 0, center !important;
  }
}

@media screen and (max-width: 959px) {
  .top_menu {
    background-position: 300px 0, center !important;
  }
}

@media screen and (max-width: 599px) {
  .top_menu {
    background-position: 150px 0, center !important;
  }
}

@media screen and (max-width: 400px) {
  .top_menu {
    background-position: 70px 0, center !important;
  }
}

.top_menu__button .v-btn.v-btn--density-default {
  margin: 0 12px;
  border-radius: 2000px;

  &:hover {
    background: var(--back-shades-dark);
  }
}

.top_menu__button__list {
  padding: 0;
  margin-top: 12px;
  border-radius: 15px !important;
  background: var(--background-white);
  box-shadow: 0 6px 20px 0 rgba(38, 54, 73, 0.32);
}

.top_menu__button__list-item {
  border-bottom: 1px solid var(--blue-shades-focus);

  &:hover {
    background: var(--blue-shades-focus);
  }
}

.top_menu__button__list-title {
  padding-left: 24px;
  padding-right: 24px;
  color: var(--corporate-main);
}
</style>
